import { Observable, Subject, take } from 'rxjs';

interface ToastRefOptions {
  dismissedByAction?: boolean;
}

export interface ToastRefAfterClosed {
  dismissedByAction: boolean;
  id: string;
  instanceId: string;
}

export class ToastRef {
  #afterClosed$ = new Subject<ToastRefAfterClosed>();
  #closeHandler: (() => void) | null = null;
  #onClosing: (() => void) | null = null;

  constructor(
    private readonly id: string,
    readonly instanceId: string = id
  ) {}

  afterClosed$(): Observable<ToastRefAfterClosed> {
    return this.#afterClosed$.asObservable().pipe(take(1));
  }

  /**
   * Requests close. When a component handler is set, it starts the exit
   * animation; otherwise only `afterClosed$` completes.
   * @returns whether a close handler ran (animation started)
   */
  close({ dismissedByAction = false }: ToastRefOptions = {}): boolean {
    if (this.#afterClosed$.closed) {
      return false;
    }

    const hadHandler = !!this.#closeHandler;

    if (this.#closeHandler) {
      this.#onClosing?.();
      this.#closeHandler();
    }

    this.#afterClosed$.next({
      dismissedByAction,
      id: this.id,
      instanceId: this.instanceId
    });
    this.#afterClosed$.complete();

    return hadHandler;
  }

  /**
   * Marks this toast as closing when exit animation starts from the component
   * (user dismiss / auto-close), so maxCount/eviction can exclude it.
   */
  notifyClosing(): void {
    this.#onClosing?.();
  }

  setOnClosing(handler: (() => void) | null): void {
    this.#onClosing = handler;
  }

  setCloseHandler(handler: (() => void) | null): void {
    this.#closeHandler = handler;
  }
}
